import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Radio } from "lucide-react";
import { Header } from "@/components/Header";
import { GameCard } from "@/components/GameCard";
import { Footer, SectionHeader } from "@/pages/Landing";
import { viewerApi } from "@/lib/api";

const HERO_BG =
  "https://images.unsplash.com/photo-1762445964939-123200d655ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Games() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerCounts, setViewerCounts] = useState({});

  useEffect(() => {
    viewerApi
      .get("/games")
      .then(({ data }) => setGames(data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const load = () =>
      viewerApi
        .get("/viewers")
        .then(({ data }) => setViewerCounts(data.counts || {}))
        .catch(() => {});
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, []);

  const live = games.filter((game) => game.status === "live");
  const upcoming = games.filter((game) => game.status === "upcoming");
  const featured = live[0] || upcoming[0];

  return (
    <div className="App">
      <Header />
      <section className="relative overflow-hidden border-b border-white/10">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-transparent" />
        <div className="relative mx-auto flex min-h-[62vh] max-w-[1400px] flex-col justify-end px-6 py-16">
          <div className="animate-fade-up max-w-2xl">
            <div className="mb-4 flex items-center gap-2">
              <span className="flex items-center gap-1.5 border border-[#007AFF]/40 bg-[#007AFF]/10 px-3 py-1">
                <Radio className="h-3.5 w-3.5 text-[#007AFF]" />
                <span className="font-display text-xs font-bold uppercase tracking-widest text-[#007AFF]">
                  {live.length > 0 ? `${live.length} Live Now` : "Live Sports Streaming"}
                </span>
              </span>
            </div>
            <h1 className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter text-white md:text-7xl">
              Every Game.<br />
              <span className="text-[#007AFF]">Live &amp; On Demand.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-white/60 md:text-lg">
              Watch upcoming matchups in crystal-clear fullscreen, then catch every replay in the YSVLive library.
            </p>
            {featured && (
              <Link
                to={`/watch/${featured.id}`}
                data-testid="hero-watch-button"
                className="mt-8 inline-flex items-center gap-2 bg-[#007AFF] px-7 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#0062cc] focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
              >
                {featured.status === "live" ? "Watch Live" : "Watch Featured"}
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-14">
        {loading ? (
          <p className="font-display text-lg uppercase tracking-widest text-white/40">Loading games…</p>
        ) : (
          <>
            {live.length > 0 && (
              <div className="mb-14">
                <SectionHeader title="Live Now" />
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {live.map((game) => (
                    <GameCard key={game.id} game={game} viewers={viewerCounts[game.id] || 0} />
                  ))}
                </div>
              </div>
            )}

            <SectionHeader title="Upcoming Games" />
            {upcoming.length === 0 ? (
              <p className="text-white/40">No upcoming games scheduled yet. Check back soon.</p>
            ) : (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="upcoming-grid">
                {upcoming.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
